import { Download, ShieldCheck, FolderOpen, ToggleRight, MousePointerClick, Pin, Loader2 } from "lucide-react";
import { useExtensionDownload } from "@/lib/useExtensionDownload";

const STEPS = [
  { icon: FolderOpen, title: "Download & unzip", body: "Click the button and unzip the file into a folder you'll keep (e.g. Documents)." },
  { icon: ToggleRight, title: "Turn on Developer mode", body: "In Chrome, open chrome://extensions and switch on 'Developer mode' (top right)." },
  { icon: Pin, title: "Load unpacked", body: "Click 'Load unpacked', pick the nyscef-docketzip folder, then pin the extension." },
  { icon: MousePointerClick, title: "Open a case & click", body: "Open a NYSCEF DocumentList or WebCivil 'eFiled Documents Detail' page, click DocketZip, pick the PDFs, then click Download ZIP." },
];

export default function InstallGuide() {
  const { download, busy } = useExtensionDownload();
  return (
    <section className="grid lg:grid-cols-12 gap-10 items-start" data-testid="install-guide">
      <div className="lg:col-span-5 space-y-6 fade-up">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
          <ShieldCheck size={13} /> Runs in your browser
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
          Every filing in a case. <span className="text-blue-600">One ZIP.</span>
        </h1>
        <p className="text-slate-600 text-base leading-relaxed max-w-md">
          NYSCEF uses a Cloudflare bot check that blocks cloud servers. So DocketZip runs as a Chrome extension inside
          your own browser session. It reads the docket list, gets each PDF, and names it
          <code className="font-mono text-[13px] bg-slate-100 px-1.5 py-0.5 rounded mx-1">001_Summons_Complaint.pdf</code>
          style. Every export is saved below.
        </p>
        <button
          data-testid="download-extension-btn"
          onClick={download}
          disabled={busy}
          className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-[#1E293B] text-white font-semibold hover:bg-blue-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-200 shadow-sm disabled:opacity-60"
        >
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} Download Chrome extension (.zip)
        </button>
        <p className="text-xs text-slate-500 max-w-md" data-testid="extension-link-note">
          Your copy is linked to your account. Downloading again unlinks older copies, so replace the old folder in chrome://extensions.
        </p>
      </div>
      <ol className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
        {STEPS.map((s, i) => (
          <li
            key={s.title}
            data-testid={`install-step-${i + 1}`}
            className="fade-up bg-white border border-slate-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-sm transition-[border-color,box-shadow]"
            style={{ animationDelay: `${(i + 1) * 80}ms` }}
          >
            <div className="flex items-center justify-between mb-4">
              <s.icon size={20} className="text-blue-600" />
              <span className="font-mono text-xs text-amber-600 font-semibold">STEP 0{i + 1}</span>
            </div>
            <h3 className="font-heading font-semibold text-base mb-1">{s.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
